import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { SEARCH_RESULT_PER_PAGE_LIMIT } from '@/features/searchV2/constants';
import { SEARCH_KEYWORDS_OBJECT } from '@/features/searchV2/constants/SearchKeywords.constants';
import { SEARCH_LOCATIONS_OBJECT } from '@/features/searchV2/constants/SearchLocations.constants';

import { persistResolvedLocationForAnonymous } from '@/features/searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';
import { getSearchPerformedTrackingProps, trackSearchPerformed } from '@/features/searchV2/helpers/tracking';
import { SearchKeywordWithSlug } from '@/features/searchV2/types/SearchKeywords.types';
import { SearchLocationWithSlug } from '@/features/searchV2/types/SearchLocations.types';
import { SearchContext } from '@/features/searchV2/types/tracking';
import { filterEmptyValues, isSistaminuten, promiseWrapper, server, trackPageCio, url } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { locationSlug, slug } from '@/helpers/urlTs';
import { _s } from '@/locale';
import { SearchState, SEARCH_ACTION } from '@/reducers/searchV2Reducer';
import { placeService } from '@/services';
import { AppDispatch } from '@/store';
import { Tracking } from '@/types/analytics/';
import { resolvedLocationSchema, SearchPlace } from '@/types/api/services/search';
import { searchFeatureFlags, UrlSearchState, urlSearchStateSchema, URL_FILTER_ID_MAP } from '@/types/state/search';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
  SEARCH_PAGE_VIEWS,
} from './useSearch.constants';

export const positionSchema = z
  .string()
  .regex(
    /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)[-+][-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
    "Invalid position format. Must be in the format 'latitude-longitude', where latitude is between -90 and 90 and longitude is between -180 and 180.",
  );

export type Position = z.infer<typeof positionSchema>;

export const searchTypeSchema = z.enum([
  'default',
  'sistaminuten',
  'deals',
  'deals_preview',
  'deals_sistaminuten',
  'brand_deals',
]);

export type SearchType = z.infer<typeof searchTypeSchema>;
export type SearchView = (typeof SEARCH_PAGE_VIEWS)[number]['view'];

// Legacy /api/search emits `topSearch: { bidId: null }` for a promoted place it could not
// attribute; only a real bid id may be reported.
export function collectBiddingBidIds(topSearch: { places?: SearchPlace[] } | undefined): number[] {
  const bidIds = new Set<number>();
  for (const place of topSearch?.places ?? []) {
    if (place?.topSearch?.bidId) {
      bidIds.add(place.topSearch.bidId);
    }
  }
  return Array.from(bidIds);
}

// The backend sends the profile-page bucket only with the last page of results, and cached pages
// are never refetched, so only a last-page response or a new search may replace it — otherwise
// fetching an earlier page wipes the bucket the cached last page shows. The last-page test has to
// agree with `hasNextPage`, which gates where the SERP renders the bucket.
export function nextProfilePages(
  current: SearchState['profilePages'],
  received: SearchState['profilePages'] | undefined,
  { resetAllSearchItems, page, results }: { resetAllSearchItems: boolean; page: number; results: number },
): SearchState['profilePages'] {
  const isLastPage = (page + 1) * SEARCH_RESULT_PER_PAGE_LIMIT >= results;
  return resetAllSearchItems || isLastPage ? received ?? [] : current;
}

export function isDealsSearch(type: SearchType) {
  switch (type) {
    case 'deals':
    case 'deals_preview':
    case 'deals_sistaminuten':
    case 'brand_deals':
      return true;
    default:
      return false;
  }
}

export function resolvePerformSearchParams(
  params: UrlSearchState,
  urlState: UrlSearchState,
  searchState: { bounds?: string },
  options: { bounds?: string },
) {
  const q = 'q' in params ? params.q : urlState.q;
  const location = 'location' in params ? params.location : urlState.location;
  const page = 'page' in params ? params.page : urlState.page;
  const sort = 'sort' in params ? params.sort : urlState.sort;
  const explain = 'explain' in params ? params.explain : urlState.explain;
  const prefs = 'prefs' in params ? params.prefs : urlState.prefs;
  const lat = 'lat' in params ? params.lat : urlState.lat;
  const lon = 'lon' in params ? params.lon : urlState.lon;

  const updatedKeyword = q !== urlState.q;
  const updatedLocation = location !== urlState.location;
  const updatedPage = page !== urlState.page;
  const updatedSearchQuery = shouldSyncClientSearchQuery(params, urlState);
  const updatedBounds = options.bounds !== undefined && options.bounds !== searchState.bounds;
  const resetSearch = (updatedKeyword || updatedLocation || updatedSearchQuery || updatedBounds) && !updatedPage;

  return {
    q,
    location,
    page,
    sort,
    explain,
    prefs,
    lat,
    lon,
    updatedKeyword,
    updatedLocation,
    updatedPage,
    updatedSearchQuery,
    updatedBounds,
    resetSearch,
  };
}

export function filterOnlyRequiredSearchParams(params) {
  if (locationSlug(params.location) === DEFAULT_MY_LOCATION_KEYWORD) {
    delete params.location;
  }

  const fieldsRequiredOnServer = [
    'q',
    'location',
    'position',
    'prefs',
    'bounds',
    'orderBy',
    'version',
    'startDate',
    'endDate',
    'timeOfDay',
    'sort',
    'page',
    'extend',
    'extendedSearchExperiment',
    'extendPage',
    'offset',
    'extendSearchIteration',
    'explain',
    'bundles',
    'startingScoreVersion',
    'searchFeatureFlags',
    'keywordSubmitted',
  ];

  // delete extra keys
  Object.keys(params)
    .filter((key) => fieldsRequiredOnServer.indexOf(key) === -1)
    .forEach((key) => {
      delete params[key];
    });

  return filterEmptyValues(params);
}

export function trackSearchResultsCustomerIO(params: UrlSearchState, search: SearchState) {
  const {
    search_query,
    search_location_city,
    search_location_filter,
    nbr_search_results,
    search_filters,
    campaigns_shown,
  } = getSearchPerformedTrackingProps({
    urlState: params,
    searchState: search,
    context: search.searchContext || 'free_search',
    screenName: search.isSistaminutenSearch ? 'smt_search_results' : 'search_results',
  });

  const screen_name: Tracking.ScreenName = search.isSistaminutenSearch ? 'smt_search_results' : 'search_results';

  const cioData: any = {
    campaigns_shown,
    search_filters,
    search_location_city,
    search_location_filter,
    search_query,
    nbr_search_results,
  };
  if (search.isSistaminutenSearch) {
    cioData.is_sistaminuten = true;
  }
  trackPageCio(screen_name, cioData);
}

export async function requestAndSetSearchResults(
  {
    type,
    view,
    params,
    state,
    resetAllSearchItems,
    bounds,
    keywordSubmitted,
    searchContext,
  }: {
    type: SearchType;
    view: SearchView;
    params: UrlSearchState;
    state: SearchState;
    resetAllSearchItems: boolean;
    bounds?: string;
    keywordSubmitted?: boolean;
    searchContext?: SearchContext;
  },
  dispatch: AppDispatch,
  onSearchPerformed: () => void,
) {
  params = withNearMeDefaultSort(type, params);

  let _bounds = (() => {
    /**
     * when updating location bounds are set to null
     * to remove the bounds from the search request
     * and reset it to undefined in search state
     */
    if (bounds === null) {
      return undefined;
    }

    /**
     * otherwise keep searching with the current bounds
     * in new search request if no new bounds are provided
     */
    return bounds ?? state.bounds;
  })();

  const _params = filterOnlyRequiredSearchParams({ ...params, bounds: _bounds, keywordSubmitted });

  dispatch({ type: SEARCH_ACTION.SET_BOUNDS, payload: _bounds });
  dispatch({ type: SEARCH_ACTION.SET_FETCHING, payload: true });

  const searchEndpoint = type === 'sistaminuten' ? '/search/last-minute' : '/search/new';
  const screenName: Tracking.ScreenName = (() => {
    if (type === 'sistaminuten') {
      if (view === 'map') {
        return 'smt_search_results_map';
      }
      return 'smt_search_results';
    }
    if (view === 'map') {
      return 'search_results_map';
    }
    return 'search_results';
  })();

  const { data: response, error } = await promiseWrapper(
    server.request.get(searchEndpoint, { params: _params, paramsSerializer: url.serialize }),
  );

  if (error) {
    toast(
      ({ closeToast }) => (
        <Snackbar type="danger" label={_s('serverError')} action={<button onClick={closeToast}>OK</button>} />
      ),
      { autoClose: 3000 },
    );
    dispatch({ type: SEARCH_ACTION.SET_FETCHING, payload: false });

    trackMpEvent({
      name: 'error_shown',
      properties: {
        screen_name: screenName,
        error_code: error?.response.status ? `http_${error?.response?.status}` : undefined,
        error_type: 'search_error',
      },
    });

    return;
  }

  // Persist the backend-resolved location for logged-out users (no-op when logged in).
  const resolvedLocation = resolvedLocationSchema.safeParse(response?.data?.resolvedLocation).success
    ? response.data.resolvedLocation
    : undefined;

  if (resolvedLocation) {
    persistResolvedLocationForAnonymous(resolvedLocation);
  }

  if (isSistaminuten() && response.data?.topSearch) {
    delete response.data?.topSearch;
  }

  const {
    hits,
    topSearch = {},
    places,
    profilePages,
    version,
    extendSearchIteration,
    showInjectionNotice = false,
    debugLocation = undefined,
    boundsFit = undefined,
    isIndexable = undefined,
  } = response.data;
  const biddingBidIds = collectBiddingBidIds(topSearch);

  if (biddingBidIds.length > 0) {
    placeService.storeImpressions(biddingBidIds);
  }

  const placesResult = (() => {
    if (resetAllSearchItems) {
      return { [params.page ?? 0]: [...(places || [])] };
    }

    return { ...state.places, [params.page ?? 0]: [...(places || [])] };
  })();

  const _searchContext = searchContext ?? state.searchContext ?? 'free_search';

  const payload: SearchState = {
    places: placesResult,
    results: hits?.value || 0,
    topSearch: topSearch || [],
    profilePages: nextProfilePages(state.profilePages, profilePages, {
      resetAllSearchItems,
      page: Number(params.page) || 0,
      results: hits?.value || 0,
    }),
    showInjectionNotice,
    extendSearchIteration: !isNaN(extendSearchIteration) ? extendSearchIteration : 0,
    isSistaminutenSearch: type === 'sistaminuten',
    version,
    debugLocation: debugLocation,
    boundsFit: boundsFit,
    isIndexable: isIndexable,
  };

  if (type === 'sistaminuten') {
    payload['extendSearchDisabled'] = true;
  }

  trackSearchResultsCustomerIO(params, { ...state, ...payload });

  onSearchPerformed();

  trackSearchPerformed(
    getSearchPerformedTrackingProps({
      urlState: params,
      searchState: payload,
      context: _searchContext,
      screenName: screenName,
    }),
  );

  /**
   * We never set searchContext to 'navigation' in the search state since that
   * should only be triggered by the navigation itself.
   * If the searchContext is not set in state, we set it to 'free_search' by default.
   */
  if (_searchContext === 'navigation') {
    payload['searchContext'] = 'free_search';
  } else {
    payload['searchContext'] = _searchContext;
  }

  dispatch({ type: SEARCH_ACTION.SET_RESULTS, payload });
}

export async function requestAndSetExtendedSearchResults(
  {
    type,
    params,
    state,
  }: {
    type: SearchType;
    params: UrlSearchState;
    state: SearchState;
  },
  dispatch: AppDispatch,
) {
  params = withNearMeDefaultSort(type, params);
  const { page } = params;
  const { extendSearchIteration = 0 } = state;
  const _params = {
    ...filterOnlyRequiredSearchParams({ ...params }),
    extend: true,
    limit: SEARCH_RESULT_PER_PAGE_LIMIT - state.places?.[page]?.length || 0,
    extendSearchIteration: !isNaN(extendSearchIteration) ? extendSearchIteration + 1 : 0,
  };

  dispatch({ type: SEARCH_ACTION.SET_EXTEND_FETCHING, payload: true });

  const searchEndpoint = type === 'sistaminuten' ? '/search/last-minute' : '/search/new';

  const { data: response, error } = await promiseWrapper(
    server.request.get(searchEndpoint, { params: _params, paramsSerializer: url.serialize }),
  );

  if (error) {
    toast(
      ({ closeToast }) => {
        const handleOnClick = () => {
          closeToast();
          dispatch({ type: SEARCH_ACTION.SET_EXTEND_FETCHING, payload: false });
        };
        return (
          <Snackbar type="danger" label={_s('serverError')} action={<button onClick={handleOnClick}>OK</button>} />
        );
      },
      { onClose: () => dispatch({ type: SEARCH_ACTION.SET_EXTEND_FETCHING, payload: false }) },
    );
    return;
  }

  const { places: extendedPlaces = [] } = response.data;
  const results = extendedPlaces.length + state.results;

  dispatch({ type: SEARCH_ACTION.SET_EXTENDED_RESULTS, payload: { page, extendedPlaces, results } });
}

const URL_SEARCH_PARAM_KEYS = [
  'startDate',
  'endDate',
  'timeOfDay',
  'prefs',
  'sort',
  'page',
  'lat',
  'lon',
  'sistaminuten',
  'explain',
  'extendedSearchExperiment',
  'q',
  'location',
] as const;

const SEARCH_PARAM_KEYS_SERVER_ONLY = [
  'orderBy',
  'version',
  'position',
  'extendedSearchExperiment',
  'searchFeatureFlags',
] as const;

export function buildServerRequestSearchParams(
  requestParams: UrlSearchState,
  urlState: UrlSearchState,
  searchState: SearchState,
) {
  const params = URL_SEARCH_PARAM_KEYS.reduce(
    (acc, key) => {
      if (key in requestParams) {
        const value = tryValidateUrlParam({ key, value: requestParams[key] });
        acc[key] = value ? `${value}` : undefined;
      } else {
        const value = tryValidateUrlParam({ key, value: urlState[key] });
        acc[key] = value ? `${value}` : undefined;
      }
      return acc;
    },
    {
      startDate: undefined,
      endDate: undefined,
      timeOfDay: undefined,
      lat: undefined,
      lon: undefined,
      sistaminuten: undefined,
    } as { [key in (typeof URL_SEARCH_PARAM_KEYS)[number]]: string },
  );

  SEARCH_PARAM_KEYS_SERVER_ONLY.forEach((key) => {
    if (key in searchState) {
      params[key] = searchState[key];
    }
  });

  if (params['lat'] && params['lon'] && positionSchema.safeParse(`${params['lat']}-${params['lon']}`).success) {
    params['position'] = `${params['lat']}-${params['lon']}`;
    delete params['lat'];
    delete params['lon'];
  }

  if (requestParams['extendedSearchExperiment']) {
    params['extendedSearchExperiment'] = requestParams['extendedSearchExperiment'];
  }

  const encodeFlags = (flags: searchFeatureFlags): string => {
    searchFeatureFlags.parse(flags);
    return encodeURIComponent(JSON.stringify(flags));
  };

  if ('searchFeatureFlags' in requestParams && Object.keys(requestParams['searchFeatureFlags']).length) {
    params['searchFeatureFlags'] = encodeFlags(requestParams['searchFeatureFlags']);
  }

  return params;
}

export function buildUrlSearchParams(
  requestParams: UrlSearchState,
  urlState: UrlSearchState,
  isIndexable: boolean,
): URLSearchParams {
  const urlSearchParams = new URLSearchParams();

  Object.entries({ ...urlState, ...requestParams }).forEach(([key, value]) => {
    const isServerKey = SEARCH_PARAM_KEYS_SERVER_ONLY.some((_key) => _key === key);

    if (isServerKey) {
      return;
    }

    // skip empty, undefined values
    if (!value) {
      return;
    }

    if (key === 'prefs') {
      const selected = `${value}`.split(',').map((id) => ({ id: +id }));

      for (const filter of selected) {
        if (URL_FILTER_ID_MAP[filter.id]) {
          urlSearchParams.append(URL_FILTER_ID_MAP[filter.id], '1');
        }
      }
    } else if (key === 'page') {
      const urlPageNumber = Number(value) + 1;
      if (!isNaN(urlPageNumber)) {
        urlSearchParams.set(key, `${urlPageNumber}`);
      }
    } else if (key === 'q' || key === 'location') {
      if (!isIndexable) {
        urlSearchParams.set(key, `${value}`);
      }
    } else {
      urlSearchParams.set(key, `${value}`);
    }
  });

  return urlSearchParams;
}

/**
 * Check if search contains any search params, and if any new search params are added that needs sync url
 */
export function shouldSyncClientSearchQuery(requestParams: UrlSearchState, urlState: UrlSearchState): boolean {
  let sync = false;

  const changedSearchParams = URL_SEARCH_PARAM_KEYS.filter((key) => requestParams[key] !== undefined);

  for (const key of changedSearchParams) {
    if (requestParams[key] !== urlState[key]) {
      sync = true;
      break;
    }
  }

  return sync;
}

/**
 * A "near me" search without an explicit sort defaults to sorting by closest.
 * Sistaminuten keeps its own default sort.
 */
export function isNearMeWithoutSort(type: SearchType, location: string | undefined, sort: unknown): boolean {
  return type === 'default' && !sort && locationSlug(location || '') === DEFAULT_MY_LOCATION_KEYWORD;
}

/**
 * Fills in the near-me default so the request and tracking see the sort the results are ordered by,
 * while the URL keeps no sort and the sort chip stays in its default state.
 */
export function withNearMeDefaultSort<T extends { location?: string; sort?: unknown }>(type: SearchType, params: T): T {
  return isNearMeWithoutSort(type, params.location, params.sort) ? { ...params, sort: 'closest' } : params;
}

export function getSearchUrlParameters(props): UrlSearchState {
  let { q = '', location = '' } = props.match.params;
  if (!q && props.search && props.search.q) {
    q = props.search.q || '';
  }
  if (!q && url.getParamsFromGET()['q']) {
    q = url.getParamsFromGET()['q'] || '';
  }

  if (!location && props.search && props.search.location) {
    location = props.search.location || '';
  }
  if (!location && url.getParamsFromGET()['location']) {
    location = url.getParamsFromGET()['location'] || '';
  }

  q = q.toString().trim();
  location = location.toString().trim();

  const urlPageNumber = Number(url.getParamsFromGET()['page']);

  return {
    q:
      q.toLowerCase() !== DEFAULT_SEARCH_KEYWORD
        ? url.decodeURIComponentWithPercent(!props.fromMegaMenu ? getMatchingKeyword(q) : q)
        : '',
    location:
      location.toLowerCase() !== DEFAULT_SEARCH_LOCATION && location.toLowerCase() !== 'var'
        ? url.decodeURIComponentWithPercent(!props.fromMegaMenu ? getMatchingLocation(location) : location)
        : '',
    //@ts-ignore
    prefs: url.getFilters() || undefined,
    page: isNaN(urlPageNumber) ? undefined : Math.max(0, urlPageNumber - 1),
    startDate: url.getParamsFromGET()['startDate'],
    endDate: url.getParamsFromGET()['endDate'],
    extendPage: url.getParamsFromGET()['extendPage'],
    extend: url.getParamsFromGET()['extend'],
    offset: url.getParamsFromGET()['offset'],
    extendSearchIteration: !isNaN(url.getParamsFromGET()['extendSearchIteration'])
      ? parseInt(url.getParamsFromGET()['extendSearchIteration'])
      : 0,
    timeOfDay: url.getParamsFromGET()['timeOfDay'],
    sort: url.getParamsFromGET()['sort'],
    lat: url.getParamsFromGET()['lat'],
    lon: url.getParamsFromGET()['lon'],
    explain: url.getParamsFromGET()['explain'],
    startingScoreVersion: url.getParamsFromGET()['startingScoreVersion'],
    sistaminuten: url.getParamsFromGET()['sistaminuten'],
  };
}

export function tryValidateUrlParam(param) {
  if (param.key === 'page' && param.value) {
    const valid = urlSearchStateSchema.safeParse({ page: Number(param.value) });
    return valid.success ? valid.data.page : undefined;
  }

  if (param.key === 'timeOfDay' && param.value) {
    const valid = urlSearchStateSchema.safeParse({ timeOfDay: param.value });
    return valid.success ? valid.data.timeOfDay : undefined;
  }

  if (param.key === 'sort' && param.value) {
    const valid = urlSearchStateSchema.safeParse({ sort: param.value });
    return valid.success ? valid.data.sort : undefined;
  }

  return param.value;
}

export function getMatchingKeyword(keyword: string): string {
  const foundMatch = SEARCH_KEYWORDS_OBJECT[slug(keyword)];
  return foundMatch?.label ?? keyword;
}

export function getMatchingLocation(location: string): string {
  const foundMatch = SEARCH_LOCATIONS_OBJECT[locationSlug(location)];
  return foundMatch?.label ?? location;
}

export function getKeywordWithSlug(keyword: string = DEFAULT_SEARCH_KEYWORD): SearchKeywordWithSlug | undefined {
  if (keyword === '') keyword = DEFAULT_SEARCH_KEYWORD;
  return SEARCH_KEYWORDS_OBJECT[slug(keyword)];
}

export function getLocationWithSlug(location: string = DEFAULT_SEARCH_LOCATION): SearchLocationWithSlug | undefined {
  if (location === '') location = DEFAULT_SEARCH_LOCATION;
  return SEARCH_LOCATIONS_OBJECT[locationSlug(location)];
}
